/**
 * Операторские органы управления: выключатель работы публикаций и ручная правка сетки
 * слотов (создать, снять свободный, перенести пост в другой слот).
 *
 * РЕЖИМ ПУБЛИКАЦИЙ ВЫБИРАЕТ КАНАЛ (решение 10.09.2026). `live` — посты уходят в боевой
 * канал бренда, `test` — в его тестовый канал. До 10.09 тестовый режим значил «в Telegram
 * не уходит ничего», и состояния звались `running`/`paused`; строки базы с прежними словами
 * читаются как `live`/`test` (`services/backend/src/domain/publication-dispatch.ts`). Какое
 * ПОЛЕ конфига стоит за режимом, решает только `resolveChannelId` в `@smm/brand-config`:
 * в профиле dev оба режима дают тестовый канал, и боевого dev не видит никогда.
 *
 * РЕЖИМ У КАЖДОГО БРЕНДА СВОЙ (2026-10-14, docs/multi-brand.md §3). Бренды живут в одной
 * установке, но переключение одного не открывает и не закрывает канал другого: общий
 * рубильник перевёл бы новый бренд без боевого канала в `live` вместе с geoboost, и его
 * одобрения отказывали бы, а тестовый канал стал бы недоступен.
 *
 * ПЕРЕНОС СЛОТА — ЭТО ПОВТОРНОЕ ОДОБРЕНИЕ. Подписанный ключ несёт начало слота и крайний
 * срок внутри подписи (§Ключ 1.3), поэтому «переставить время» невозможно: старый ключ
 * отзывается, новый выпускается, и перенос обязан пройти те же проверки, что одобрение.
 * Отдельно — окно свежести: перенос НА ПОЗЖЕ способен вынести материал за его срок, и это
 * отказ, а не молчаливая перестановка.
 */

import * as z from 'zod';
import { BRAND_RE, CHAT_ID_RE, CONTENT_HASH_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';
import { LOCAL_MINUTE_RE } from './ui.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const ContentHash = z.string().regex(CONTENT_HASH_RE);

// ─────────────────────────────────────────────────────────────────────────────
// Режим публикаций

/**
 * `live` — одобрение подписывает БОЕВОЙ канал бренда, и публикатору выдаются только работы
 * на него; `test` — то же для тестового канала.
 *
 * Канал входит в подписанный payload ключа (§Ключ 1.3), поэтому режим действует дважды и
 * по-разному. Пост, одобренный в тестовом режиме, подписан на тестовый канал и в боевой не
 * попадёт никогда — ни после переключения, ни при сломанном бэкенде: публикатор сверяет
 * подписанный chat_id со своим конфигом. Пост, одобренный в боевом режиме, в тестовом не
 * выдаётся и ждёт возврата в боевой — ровно так, как раньше ждал снятия паузы.
 */
export const PUBLICATION_DISPATCH_STATES = ['live', 'test'] as const;
export const PublicationDispatchStateSchema = z.enum(PUBLICATION_DISPATCH_STATES);
export type PublicationDispatchState = z.output<typeof PublicationDispatchStateSchema>;

/**
 * Режим плюс то, что делает его показ осмысленным.
 *
 * `waiting_jobs` — сколько работ уже дождались своего слота и стоят невыданными, потому что
 * подписаны на канал ДРУГОГО режима. Это ровно те посты, которые уйдут сразу после
 * переключения, и единственный честный ответ на вопрос «что я держу».
 *
 * `in_flight_jobs` — сколько работ канала ТЕКУЩЕГО режима уже у публикатора (`leased`) или
 * отправляются (`sending`). Переключение их не держит: выданная работа получит отказ
 * `mode_changed`, только если публикатор ещё не записал намерение отправки, а
 * отправляющаяся уходит. Человек, переключающий режим, чтобы остановить пост, обязан видеть
 * это число ДО подтверждения.
 *
 * `changed_at`/`changed_by_login` пусты, пока режим никто не трогал: умолчание — `test`
 * (с 15.09.2026; до того — `live`), и приписывать его человеку нельзя.
 */
export const PublicationDispatchSchema = z.strictObject({
  /** Бренд, чей режим показан: с 2026-10-14 режим у каждого бренда свой (docs/multi-brand.md §3). */
  brand: z.string().regex(BRAND_RE),
  state: PublicationDispatchStateSchema,
  changed_at: IsoUtcMs.nullable(),
  changed_by_login: z.string().nullable(),
  note: z.string().nullable(),
  waiting_jobs: z.int().nonnegative(),
  in_flight_jobs: z.int().nonnegative(),
});
export type PublicationDispatch = z.output<typeof PublicationDispatchSchema>;

export const SetPublicationDispatchBodySchema = z.strictObject({
  /** Чей режим переключается. Переключение одного бренда не трогает канал другого. */
  brand: z.string().regex(BRAND_RE),
  state: PublicationDispatchStateSchema,
  /** Зачем переключили. Не обязателен, но именно он через неделю объясняет тишину в канале. */
  note: z.string().min(1).max(500).nullable(),
});
export type SetPublicationDispatchBody = z.output<typeof SetPublicationDispatchBodySchema>;

// ─────────────────────────────────────────────────────────────────────────────
// Слоты руками

/**
 * Новый слот задаётся МЕСТНОЙ МИНУТОЙ бренда, а не инстантом: человек ставит «четверг,
 * 12:00» в календаре канала, и хранится в `publication_slot.slot_local` ровно это
 * намерение. Перевод в инстант и отметку неоднозначности (перевод часов назад) делает
 * бэкенд — зона бренда есть у него и нет у браузера.
 */
export const CreateSlotBodySchema = z.strictObject({
  brand: z.string().regex(BRAND_RE),
  slot_local: z.string().regex(LOCAL_MINUTE_RE),
});
export type CreateSlotBody = z.output<typeof CreateSlotBodySchema>;

/**
 * Перенос поста в другой слот. Тело — то же, что у одобрения, и по той же причине:
 * `expected_content_hash` доказывает, что человек переносит РОВНО ТО, что видел, а не
 * версию, переписанную между показом и перетаскиванием.
 *
 * `expected_mode` ОБЯЗАТЕЛЕН (15.09.2026), в отличие от одобрения. Перенос — это отзыв
 * старого ключа и выпуск нового, а новый ключ подписывается на канал ТЕКУЩЕГО режима.
 * Пост, одобренный в тестовый канал и перетащенный в календаре, пока режим боевой, был бы
 * молча переподписан на боевой канал — без диалога, который называет канал. Бэкенд
 * (`domain/slots.ts`) отказывает, если режим поста и текущий режим расходятся; поле здесь
 * ловит вторую половину — режим сменился между показом сетки и перетаскиванием.
 *
 * `expected_brand` и `expected_chat_id` ОБЯЗАТЕЛЬНЫ (2026-10-25, изоляция брендов): бренд
 * поста, который человек тащил, и канал режима, который показывал календарь. Смысл и отказы
 * (`brand_mismatch`, `channel_mismatch`) — те же, что у одобрения, см. шапку `decisions.ts`.
 * Режим не заменяет канал: он совпадёт и тогда, когда канал режима сменили в конфиге.
 */
export const MoveDraftBodySchema = z.strictObject({
  slot_at: IsoUtcMs,
  expected_content_hash: ContentHash,
  expected_mode: PublicationDispatchStateSchema,
  expected_brand: z.string().regex(BRAND_RE),
  expected_chat_id: z.string().regex(CHAT_ID_RE),
});
export type MoveDraftBody = z.output<typeof MoveDraftBodySchema>;

/**
 * Ответ переноса. Отличается от ответа одобрения одним полем — `previous_slot_at`: ключ и
 * работа НОВЫЕ, и человеку стоит видеть, откуда пост уехал, чтобы отличить свой перенос от
 * чужого.
 */
export const MoveDraftResponseSchema = z.strictObject({
  status: z.literal('scheduled'),
  publication_id: Uuid,
  key_id: Uuid,
  slot_at: IsoUtcMs,
  previous_slot_at: IsoUtcMs,
  content_hash: ContentHash,
});
export type MoveDraftResponse = z.output<typeof MoveDraftResponseSchema>;
